import { CHAT_SEARCH_MIN_QUERY_LENGTH } from "@frontend-shared/constants";
import type { IChatListObject } from "@frontend-shared/types";
import { getDateFormat } from "@frontend-shared/utils/date";
import { format } from "date-fns";
import type { RefCallback } from "react";
import { useTranslation } from "react-i18next";

import { Message, SearchEmpty } from "@/components/icons";
import { SpinningLoader } from "@/components/SpinningLoader";
import { Button } from "@/components/ui/button";
import { CommandGroup, CommandItem } from "@/components/ui/command";
import { getAutoDirection } from "@/lib/direction";

interface ISearchResultsProps {
  isLoading: boolean;
  isError: boolean;
  isBelowMinLength: boolean;
  chats: IChatListObject[];
  isFetchingNextPage: boolean;
  onRetry: () => void;
  onSelectChat: (chat: IChatListObject) => void;
  loadMoreRef: RefCallback<HTMLDivElement>;
}

export const SearchResults = ({
  isLoading,
  isError,
  isBelowMinLength,
  chats,
  isFetchingNextPage,
  onRetry,
  onSelectChat,
  loadMoreRef,
}: ISearchResultsProps) => {
  const { t, i18n } = useTranslation("chat");

  // NOTE: NSAI-4334 — checked before the loading state so shortening a query
  // back down to one character swaps straight to the hint. Rendering
  // "no results" here would be a lie: the server never looked.
  if (isBelowMinLength) {
    return (
      <div
        className="h-full flex items-center justify-center flex-col gap-4 text-primary"
        data-testid="search-chats-min-length-hint"
      >
        <SearchEmpty className="h-8 w-8" />
        <span className="text-sm text-foreground-secondary">
          {t("search.min_length_hint", {
            minLength: CHAT_SEARCH_MIN_QUERY_LENGTH,
          })}
        </span>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="h-full flex items-center justify-center"
        data-testid="search-chats-loading-state"
      >
        <SpinningLoader />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="h-full flex items-center justify-center flex-col gap-4"
        data-testid="search-chats-error"
      >
        <span className="text-sm text-foreground-secondary">
          {t("search.error_loading")}
        </span>
        <Button data-testid="search-chats-retry" onClick={onRetry}>
          {t("search.retry")}
        </Button>
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div
        className="h-full flex items-center justify-center flex-col gap-4 text-primary"
        data-testid="search-chats-empty"
      >
        <SearchEmpty className="h-8 w-8" />
        <span className="text-sm">{t("search.no_results")}</span>
      </div>
    );
  }

  return (
    <CommandGroup className="p-0" data-testid="search-chats-items-group">
      {chats.map((chat) => (
        <CommandItem
          key={chat.uuid}
          value={chat.uuid}
          onSelect={() => onSelectChat(chat)}
          className="rounded-lg group py-0 px-2.5"
          data-testid={`search-chats-item-${chat.uuid}`}
        >
          <div className="flex items-center gap-2 border-b group-last-of-type:border-b-0 grow  py-3 group truncate">
            <div className="p-2 border rounded-lg shrink-0 group-data-[selected=true]:border-transparent bg-icon-background group-data-[selected=true]:bg-transparent">
              <Message className="h-5 w-5 text-foreground" />
            </div>
            <div className="flex flex-col truncate">
              <p
                dir={getAutoDirection({ content: chat.title })}
                className="text-sm font-medium text-start group-data-[selected=true]:text-primary truncate"
              >
                {chat.title}
              </p>
              <p className="text-xs text-foreground-secondary group-data-[selected=true]:text-primary">
                {t("search.chat_created_on")}{" "}
                {format(new Date(chat.createdAt), getDateFormat(i18n.language))}
              </p>
            </div>
          </div>
        </CommandItem>
      ))}
      <div
        ref={loadMoreRef}
        className="px-2.5 py-3"
        data-testid="search-chats-load-more"
      >
        {isFetchingNextPage && (
          <div className="flex items-center justify-center">
            <SpinningLoader />
          </div>
        )}
      </div>
    </CommandGroup>
  );
};
